import { getConfigByEnvironmentContext } from "./url-mapper";
import { ModuleName } from "./types";

declare global {
  interface Window {
    Cypress?: unknown;
    iframeConfig?: {
      iframe: boolean;
      environment: string;
      consumerId: string;
      customerSystem: string;
      [key: string]: unknown;
    };
  }
}

// eslint-disable-next-line @typescript-eslint/naming-convention
declare const __webpack_public_path__: string;

/**
 * Retrieves the environment configuration for the current module and environment.
 *
 * This function determines the module and environment based on the execution context
 * and returns an object containing the relevant environment variables.
 *
 * @param {ModuleName} [moduleName] - Module Name
 * @returns {T} The environment configuration object.
 *
 * @example
 * // Retrieve configuration for a module named
 * const envConfigWithContext = getEnvConfig<MyConfig>('cxm');
 * console.log(envConfigWithContext.API_URL);
 */
export const getEnvConfig = <T extends Record<string, any>>(
  moduleName: ModuleName
): T => {
  // Detect Cypress (component or e2e)
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const isCypress =
    typeof window !== "undefined" &&
    (window.Cypress ||
      (window.location &&
        window.location.pathname &&
        window.location.pathname.startsWith("/__cypress")));

  // Check if we're running in an iframe with environment config
  const isIframe =
    typeof window !== "undefined" &&
    window.iframeConfig?.iframe === true &&
    window.iframeConfig?.environment;

  // Use window.location.origin for environment detection instead of webpack public path
  // This ensures proper environment detection even when webpack uses relative paths
  // For iframe usage, we need to detect based on where the iframe content is served from
  // eslint-disable-next-line @typescript-eslint/naming-convention
  let url = __webpack_public_path__;

  // If we're in an iframe, construct URL based on the environment from iframe config
  if (isIframe && window.iframeConfig?.environment) {
    const environment = window.iframeConfig.environment;
    switch (environment) {
      case "nonprod":
        url = "https://communications-np.vinccrmnp.cloud/";
        break;
      case "prod":
        url = "https://communications.vinccrm.cloud/";
        break;
      case "local":
      default:
        url = "http://localhost:4200/";
        break;
    }
  }
  // If webpack is using relative paths (like for iframe builds),
  // construct the full URL from the current window location
  else if (typeof window !== "undefined" && url === "./") {
    url = window.location.origin + "/";
  }

  if (isCypress) {
    url = "http://localhost:4200/";
  }

  const config = getConfigByEnvironmentContext(url, moduleName);

  if (!config) {
    throw new Error(`Environment not found for the current url: ${url}`);
  }

  return config as unknown as T;
};

export default getEnvConfig;
